import { ChevronLeftIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { AudienceId } from "@sp-crm/core";
import { Content, ContentHeader } from "components/layout";
import { DeleteButton } from "components/ui/action-button";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    GetAudienceQuery,
    useDeleteAudienceMutation,
    useGetAudienceQuery,
    useGetEntitiesQuery,
    useUpdateAudienceMutation,
} from "generated/graphql";
import React, { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { AudienceMembers } from "./audience-members";
import { getProviderFromPath } from "./audience-provider-utils";
import { AudienceQueryInput } from "./audience-query-input";
import { AudienceSyncButton } from "./audience-sync-button";

export interface AudienceShowProps {
    audienceId: AudienceId;
    subtab: string;
}

export const AudienceShow: React.FC<AudienceShowProps> = props => {
    const { audienceId, subtab } = props;
    const regionId = useRegionId();
    const location = useLocation();
    const providerInfo = getProviderFromPath(location.pathname);

    const getAudienceQuery = useGetAudienceQuery({
        input: {
            id: audienceId,
            regionId,
        },
    });
    const getEntitiesQuery = useGetEntitiesQuery({}, { ...stableQueryOptions() });
    const updateAudienceMutation = useUpdateAudienceMutation();
    const deleteAudienceMutation = useDeleteAudienceMutation();
    const urlParams = new URLSearchParams(location.search);
    const initialMode = urlParams.get("initialMode") === "edit" ? "edit" : "view";

    const [mode, setMode] = useState<"view" | "edit">(initialMode);

    const [audience, setAudience] = useState<GetAudienceQuery["getAudience"] | null>(
        null,
    );

    React.useEffect(() => {
        if (getAudienceQuery.data?.getAudience) {
            setAudience(getAudienceQuery.data.getAudience);
        }
    }, [getAudienceQuery.data?.getAudience]);

    const handleAudienceChange = useCallback(
        async (value: GetAudienceQuery["getAudience"]) => {
            setMode("edit");
            setAudience(value);
        },
        [setMode, setAudience],
    );

    const handleEdit = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setMode("edit");
        },
        [setMode],
    );

    const handleClose = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setMode("view");
            setAudience(getAudienceQuery.data?.getAudience || null);
        },
        [setMode, getAudienceQuery.data?.getAudience],
    );

    const onSaveCriteria = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await updateAudienceMutation.mutateAsync({
                input: {
                    id: audienceId,
                    query: audience?.query,
                    name: audience?.name || "",
                    regionId,
                },
            });
            getAudienceQuery.refetch();
            setMode("view");
        },
        [
            setMode,
            updateAudienceMutation,
            audienceId,
            audience?.query,
            audience?.name,
            regionId,
            getAudienceQuery,
        ],
    );

    const onDelete = useCallback(async () => {
        await deleteAudienceMutation.mutateAsync({
            input: {
                id: audienceId,
                regionId,
            },
        });
        navigate(providerInfo.redirectPath);
    }, [deleteAudienceMutation, audienceId, regionId, providerInfo.redirectPath]);

    const entities = getEntitiesQuery?.data?.getEntities || [];

    if (!audience) {
        return null;
    }

    return (
        <>
            <div className="mb-2">
                <Link to={providerInfo.redirectPath} className={defaultLinkStyle}>
                    <div className="flex space-x-2 items-center">
                        <ChevronLeftIcon className="h-4 w-4" />
                        <span>Back to {providerInfo.displayName}</span>
                    </div>
                </Link>
            </div>
            <div className="space-y-4">
                <Content>
                    <ContentHeader>
                        <div className="flex items-center justify-between">
                            <span>Contact List</span>
                            <div className="flex items-center space-x-2">
                                {mode === "edit" ? (
                                    <SecondaryButton
                                        className="flex items-center space-x-2"
                                        onClick={handleClose}>
                                        <ChevronUpIcon className="w-4 h-4" />
                                        <div>Close editor</div>
                                    </SecondaryButton>
                                ) : (
                                    <SecondaryButton onClick={handleEdit}>
                                        Edit definition
                                    </SecondaryButton>
                                )}
                                <DeleteButton
                                    onClick={onDelete}
                                    backgroundColor="bg-white"
                                    confirm={{
                                        title: "Delete Contact List?",
                                        message: `Delete "${audience?.name}" contact list? This action cannot be undone.`,
                                    }}
                                />
                            </div>
                        </div>
                    </ContentHeader>
                    <AudienceQueryInput
                        mode={mode}
                        audience={audience}
                        onChange={handleAudienceChange}
                    />
                    {mode === "edit" ? (
                        <div className="flex items-center space-x-4 mt-8">
                            <PrimaryButton onClick={onSaveCriteria}>Save</PrimaryButton>
                            <SecondaryButton onClick={handleClose}>
                                Cancel
                            </SecondaryButton>
                            {updateAudienceMutation.isLoading ? <Spinner /> : null}
                        </div>
                    ) : (
                        <AudienceSyncButton
                            audienceId={audienceId}
                            audience={audience}
                            provider={providerInfo.provider}
                            providerDisplayName={providerInfo.displayName}
                            onSyncComplete={() => getAudienceQuery.refetch()}
                        />
                    )}
                </Content>
                {getAudienceQuery?.data?.getAudience?.query?.queries?.length > 0 ? (
                    <AudienceMembers
                        key={getAudienceQuery.data.getAudience.updatedAt.toString()}
                        audience={getAudienceQuery.data.getAudience}
                        entities={entities}
                        subtab={subtab}
                    />
                ) : null}
            </div>
        </>
    );
};
